import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isEmpty } from 'lodash';

import { AccessLevelIcon, ModelLayer } from '@dbt-labs/dbt-dag';
import {
  determineTrustState,
  LinkMapping,
  TrustState,
} from '@dbt-labs/metadata-shared';
import {
  CellContext,
  ColumnDef,
  Icon,
  Link,
  RyeconLink,
  SortingState,
} from '@dbt-labs/sourdough';

import {
  AccessLevel,
  AppliedModelSort,
  AppliedModelSortField,
  HealthIssueType,
  ModelAppliedFilter,
  ModelQueryHistoryStatus,
  PackageResourceType,
  RunStatus,
  SortDirection,
  SourceHealthIssueType,
} from '@/__generated__/graphql';
import {
  ACCESS_LEVEL_PARAM,
  AccessLevelFilterDropdown,
} from '@/components/ModelFilters/AccessLevelFilterDropdown';
import {
  GROUP_PARAM,
  GroupFilterDropdown,
} from '@/components/ModelFilters/GroupFilterDropdown';
import {
  HEALTH_FILTER_PARAM,
  HealthFilterDropdown,
} from '@/components/ModelFilters/HealthFilterDropdown';
import {
  MODELING_LAYER_PARAM,
  ModelingLayerFilterDropdown,
} from '@/components/ModelFilters/ModelingLayerFilterDropdown';
import {
  ALL_PACKAGES_VALUE,
  PACKAGE_NAME_PARAM,
  PackageFilterDropdown,
} from '@/components/ModelFilters/PackageFilterDropdown';
import {
  RUN_STATUS_PARAM,
  StatusFilterDropdown,
} from '@/components/ModelFilters/StatusFilterDropdown';
import { PageHeading } from '@/components/PageHeading';
import { buildRelationName } from '@/components/RelationName';
import { CustomCell } from '@/components/Table/CustomCell';
import {
  getNameLinkCell,
  getNameWithHealthLinkCell,
} from '@/components/Table/LinkCell';
import { ModelTrustSignalCell } from '@/components/Table/ModelTrustSignalCell';
import { OneLineHeader } from '@/components/Table/TableHeader';
import {
  RightAlignedTruncatedCell,
  TruncatedCell,
  TruncatedCopyLinkCell,
} from '@/components/TruncatedCell';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useFeatureFlags } from '@/context/FeatureFlagProvider';
import {
  ModelAppliedListNode,
  useDiscoveryAppliedModels,
} from '@/hooks/discovery/appliedModels';
import { useModelQueryHistoryState } from '@/hooks/discovery/modelQueryHistoryState';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { useGetLinkMapping } from '@/hooks/useGetLinkMapping';
import { useHasEnterpriseFeatures } from '@/hooks/useHasEnterpriseFeatures';
import { ALL_MODELS_FRAGMENT } from '@/pages/account/components/PopularModelsSection';
import { PageInfo } from '@/reducers/usePaginatedQuery';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { isDevelopment } from '@/typings/url';
import { formatAbsoluteLocalDate } from '@/util/date';
import { modelQueryHistorySupported } from '@/util/dbt/adapter';
import { toTitleCase } from '@/util/string';
import QueryHistoryTableHeader, {
  queryHistoryCellConfig,
} from '../QueryHistoryTableHeader';
import { INITIAL_PAGE_SIZE, ResourceFilterTable } from '../ResourceFilterTable';

type TableData = {
  name: string;
  uniqueId: string;
  rowCount: string | undefined;
  lastExecuted: string | null;
  healthIssues: HealthIssueType[];
  group: string | null;
  relation: string | null;
  lastRunStatus: RunStatus | null;
  usageQueryCount?: string;
  modelingLayer: string | null;
  access: string | null;
  contractEnforced: string | null;
};

const getTableData = (model: ModelAppliedListNode): TableData => {
  const uniqueId = parseUniqueId(model.uniqueId);
  const releaseVersion =
    !model.releaseVersion || model.releaseVersion === 'none'
      ? ''
      : ` (${model.releaseVersion})`;
  const executeCompletedAtStr = model.executionInfo?.executeCompletedAt;
  const lastRunStatus = model.executionInfo?.lastRunStatus ?? null;
  const executedAtDate = executeCompletedAtStr ? new Date(executeCompletedAtStr) : null;
  const lastExecuted = executedAtDate && formatAbsoluteLocalDate(executedAtDate);
  const healthIssues = model.healthIssues;
  // TODO: Add tooltip with UTC time
  // const lastExecutedUtc = executedAtDate && formatDateCommon(executedAtDate).utc;
  return {
    name: `${uniqueId.remainder}${releaseVersion}`,
    uniqueId: uniqueId.raw,
    lastRunStatus,
    lastExecuted: lastExecuted,
    healthIssues,
    group: model.group ?? null,
    relation: buildRelationName(model),
    usageQueryCount:
      ('usageQueryCount' in model
        ? model.usageQueryCount
        : undefined
      )?.toLocaleString() ?? undefined,
    rowCount: model.catalog?.rowCountStat?.toLocaleString() ?? undefined,
    modelingLayer: model.modelingLayer,
    access: model.access,
    contractEnforced:
      model.contractEnforced == null ? null : model.contractEnforced ? 'True' : 'False',
  };
};

const _sortableColumnNames = [
  'name',
  'lastExecuted',
  'rowCount',
  'usageQueryCount',
] as const;
type SortableColumnName = (typeof _sortableColumnNames)[number];

const SORTABLE_COLS = {
  name: AppliedModelSortField.UniqueId,
  lastExecuted: AppliedModelSortField.ExecuteCompletedAt,
  rowCount: AppliedModelSortField.RowCount,
  usageQueryCount: AppliedModelSortField.QueryUsageCount,
} satisfies Record<SortableColumnName, AppliedModelSortField>;

const SORTABLE_DATA_FIELDS = Object.entries(SORTABLE_COLS).reduce(
  (acc, [key, value]) => {
    acc[value] = key as SortableColumnName;
    return acc;
  },
  {} as Record<AppliedModelSortField, SortableColumnName>,
);

export const ModelFilterView = () => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const { accountId, environmentId, enableModelQueryHistory, isMounted } =
    useDbtCloudProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userId } = useEnvironmentScope();
  const { environmentType } = useUrlProjectParams();

  const [packageInfoLoaded, setPackageInfoLoaded] = useState(false);
  const pageSize = INITIAL_PAGE_SIZE;

  const isCatalogPreviewEnabled = hasAccountFeature('explorerAccountUI');
  // TODO: Remove isDevelopment check since only prod environments can be opted in
  const queryHistoryEnabled =
    enableModelQueryHistory && !isDevelopment(environmentType);

  useEffect(() => {
    if (
      queryHistoryEnabled &&
      isCatalogPreviewEnabled &&
      !searchParams.get('sortField') &&
      !searchParams.get('sortDir')
    ) {
      setSearchParams((prev) => {
        prev.set('sortField', AppliedModelSortField.QueryUsageCount);
        prev.set('sortDir', SortDirection.Desc);
        return prev;
      });
    }
  }, [queryHistoryEnabled, isCatalogPreviewEnabled, searchParams, setSearchParams]);

  const filter = useMemo((): Partial<ModelAppliedFilter> => {
    const qsPackageName = searchParams.get(PACKAGE_NAME_PARAM) ?? undefined;
    return {
      lastRunStatus: searchParams.get(RUN_STATUS_PARAM) as RunStatus | undefined,
      modelingLayer: searchParams.get(MODELING_LAYER_PARAM) as ModelLayer | undefined,
      packageName: isCatalogPreviewEnabled
        ? undefined
        : qsPackageName === ALL_PACKAGES_VALUE
          ? undefined
          : qsPackageName,
      access: searchParams.get(ACCESS_LEVEL_PARAM) as AccessLevel | undefined,
      group: searchParams.get(GROUP_PARAM) as string | undefined,
    };
  }, [isCatalogPreviewEnabled, searchParams]);

  const sortInfo = useMemo((): AppliedModelSort => {
    if (
      queryHistoryEnabled &&
      isCatalogPreviewEnabled &&
      !searchParams.get('sortDir') &&
      !searchParams.get('sortField')
    ) {
      return {
        direction: SortDirection.Desc,
        field: AppliedModelSortField.QueryUsageCount,
      };
    }

    return {
      direction: (searchParams.get('sortDir') as SortDirection) || SortDirection.Desc,
      field:
        (searchParams.get('sortField') as AppliedModelSortField) ||
        AppliedModelSortField.ExecuteCompletedAt,
    };
  }, [searchParams, queryHistoryEnabled, isCatalogPreviewEnabled]);

  const paginatedModelQuery = useDiscoveryAppliedModels({
    environmentId,
    userId,
    first: pageSize,
    filter,
    sort: sortInfo,
    includeQueryCount: queryHistoryEnabled,
    // This is needed to ensure that we don't get all models until package info is loaded.
    // Without this check we end up making 2 calls to get applied models.
    pause: !packageInfoLoaded && !isCatalogPreviewEnabled,
  });
  const models = paginatedModelQuery.data;

  const healthFilter = searchParams.get(HEALTH_FILTER_PARAM) as TrustState | null;

  // client-side filtering
  const filteredModels = useMemo(() => {
    if (!models || !healthFilter) return models;

    return models.filter((model) => {
      const trustState = determineTrustState(model.healthIssues);
      return trustState === healthFilter;
    });
  }, [models, healthFilter]);

  const { data, hasGroup } = useMemo(() => {
    const hasGroup = filteredModels?.some((model) => model.group !== null);
    return { data: filteredModels?.map(getTableData), hasGroup };
  }, [filteredModels]);

  const multiProjectLinkGenerator = useLinkToExplorer('multiProject');
  const multiProjectLink = multiProjectLinkGenerator({
    accountId,
    fragment: ALL_MODELS_FRAGMENT,
  });

  if (paginatedModelQuery.error) return <>{paginatedModelQuery.error.stack}</>;
  const isLoadingMore = paginatedModelQuery.fetching;
  const isInitialLoad =
    (!isMounted &&
      paginatedModelQuery.fetching &&
      paginatedModelQuery.data === undefined) ||
    (!packageInfoLoaded && !isCatalogPreviewEnabled);

  return (
    <>
      <PageHeading className="mb-10">Models</PageHeading>
      <div className="my-2 flex flex-row justify-between gap-2">
        <div className="flex flex-1 flex-wrap gap-2">
          {!isCatalogPreviewEnabled && <StatusFilterDropdown />}
          <ModelingLayerFilterDropdown />
          {!isCatalogPreviewEnabled && (
            <PackageFilterDropdown
              environmentId={environmentId}
              onLoad={() => setPackageInfoLoaded(true)}
              resourceType={PackageResourceType.Model}
            />
          )}
          <AccessLevelFilterDropdown
            label={isCatalogPreviewEnabled ? 'Model access' : 'Access Level'}
          />
          {isCatalogPreviewEnabled && <HealthFilterDropdown />}
          {isCatalogPreviewEnabled && <GroupFilterDropdown />}
        </div>
        <div>
          <Link to={multiProjectLink} isInternal buttonStyleType="primary">
            <Icon ryecon={RyeconLink} />
            All Account Models
          </Link>
        </div>
      </div>
      {!isCatalogPreviewEnabled ? (
        <LegacyModelFilterTable
          data={data}
          hasGroup={hasGroup}
          pageInfo={paginatedModelQuery.pageInfo}
          sortInfo={sortInfo}
          isInitialLoad={isInitialLoad}
          isLoadingMore={isLoadingMore}
          fetchMore={paginatedModelQuery.fetchMore}
          pageSize={pageSize}
        />
      ) : (
        <NewModelFilterTable
          data={data}
          pageInfo={paginatedModelQuery.pageInfo}
          sortInfo={sortInfo}
          isInitialLoad={isInitialLoad}
          isLoadingMore={isLoadingMore}
          fetchMore={paginatedModelQuery.fetchMore}
          pageSize={pageSize}
        />
      )}
    </>
  );
};

interface ModelFilterTableProps {
  data: TableData[] | undefined;
  hasGroup?: boolean;
  pageInfo: PageInfo<any> | undefined;
  sortInfo: AppliedModelSort;
  isInitialLoad: boolean;
  isLoadingMore: boolean;
  fetchMore: () => void;
  pageSize: number;
}

const LegacyModelFilterTable = ({
  data,
  hasGroup,
  pageInfo,
  sortInfo,
  isInitialLoad,
  isLoadingMore,
  fetchMore,
  pageSize,
}: ModelFilterTableProps) => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const { environmentId, enableModelQueryHistory, projectId } = useDbtCloudProject();
  const featureFlags = useFeatureFlags();
  const projectScope = useUrlProjectParams();
  const [, setSearchParams] = useSearchParams();

  const environmentType = projectScope.environmentType;

  const getDetailsPageLink = useLinkToExplorer('resourceDetails');
  const getLinkMapping = useGetLinkMapping();

  const [queryHistoryStateQuery] = useModelQueryHistoryState({ environmentId });
  const queryHistoryStateHolder =
    queryHistoryStateQuery.data?.environment.modelQueryHistoryState;

  const [tableKey, setTableKey] = useState(0);
  const onChangeSort = useCallback(
    (sortRule: SortingState) => {
      setTableKey((previous) => previous + 1);
      if (!sortRule?.length) {
        return;
      }
      const sortCol: string = sortRule[0].id;
      const sortDir = sortRule[0].desc ? SortDirection.Desc : SortDirection.Asc;

      setSearchParams((p) => {
        p.set('sortField', SORTABLE_COLS[sortCol as SortableColumnName]);
        p.set('sortDir', sortDir);
        return p;
      });
    },
    [setSearchParams],
  );

  // TODO: Remove isDevelopment check since only prod environments can be opted in
  const queryHistoryEnabled =
    enableModelQueryHistory && !isDevelopment(environmentType);

  const queryHistoryAvailableNotEnabled =
    hasAccountFeature('explorerQueryHistory') &&
    !queryHistoryEnabled &&
    modelQueryHistorySupported(
      queryHistoryStateQuery.data?.environment.adapterType || '',
      environmentType,
      featureFlags,
    );

  const sortDescFirst =
    queryHistoryStateHolder?.state === ModelQueryHistoryStatus.Success &&
    !queryHistoryAvailableNotEnabled;

  const columns = useMemo(
    () =>
      [
        {
          header: 'Name',
          accessorKey: 'name',
          id: 'name',
          cell: getNameLinkCell(getDetailsPageLink, projectScope),
          enableSorting: true,
        },
        {
          header: 'Health',
          accessorKey: 'healthIssues',
          cell: (
            cellProps: CellContext<
              TableData,
              (HealthIssueType | SourceHealthIssueType)[]
            >,
          ) => {
            const values = cellProps.getValue();
            const { uniqueId } = cellProps.row.original;

            /**
             * transform from an array of health issues to a link mapping
             * e.g [HealthIssueType.CautionUpstreamSources] to { CautionUpstreamSources: { to: '...', search: '...' } }
             */
            const linkMapping: LinkMapping = getLinkMapping({
              healthIssues: values,
              projectId,
              environmentType,
              uniqueId,
            });
            return (
              <ModelTrustSignalCell
                {...cellProps}
                additionalMetadata={!isEmpty(linkMapping) ? { linkMapping } : undefined}
              />
            );
          },
          size: 90,
          minSize: 90,
          enableSorting: false,
        },
        {
          header: () => <OneLineHeader title="Row count" align="right" />,
          accessorKey: 'rowCount',
          cell: RightAlignedTruncatedCell,
          size: 30,
          minSize: 120,
          enableSorting: true,
          sortDescFirst: true,
          meta: {
            align: 'right' as const,
          },
        },
        {
          header: 'Last executed',
          accessorKey: 'lastExecuted',
          cell: TruncatedCell,
          size: 80,
          minSize: 120,
          enableSorting: true,
          sortDescFirst: true,
        },
        // TODO: [GBB] gate query history META-4358
        ...(queryHistoryEnabled || queryHistoryAvailableNotEnabled
          ? [
              {
                accessorKey: 'usageQueryCount',
                header: () => {
                  return (
                    <QueryHistoryTableHeader
                      queryHistoryState={queryHistoryStateHolder?.state}
                      error={queryHistoryStateHolder?.error}
                      errorCategory={queryHistoryStateHolder?.errorCategory}
                      queryHistoryAvailableNotEnabled={queryHistoryAvailableNotEnabled}
                    />
                  );
                },
                cell: queryHistoryCellConfig(
                  queryHistoryStateHolder?.state,
                  queryHistoryAvailableNotEnabled,
                ),
                meta: {
                  align: queryHistoryAvailableNotEnabled
                    ? ('left' as const)
                    : ('right' as const),
                },
                size: 80,
                minSize: 120,
                enableSorting: sortDescFirst,
                sortDescFirst: sortDescFirst,
              } as ColumnDef<any, any>,
            ]
          : []),
        ...(hasGroup
          ? [
              {
                header: 'Owner',
                accessorKey: 'group',
                cell: TruncatedCell,
                size: 60,
                minSize: 120,
                enableSorting: false,
              },
            ]
          : []),
        {
          header: 'Relation',
          accessorKey: 'relation',
          cell: TruncatedCopyLinkCell,
          size: 120,
          enableSorting: false,
        },
      ] as ColumnDef<any, any>[],
    [
      environmentType,
      getDetailsPageLink,
      getLinkMapping,
      hasGroup,
      projectId,
      projectScope,
      queryHistoryAvailableNotEnabled,
      queryHistoryEnabled,
      queryHistoryStateHolder?.error,
      queryHistoryStateHolder?.errorCategory,
      queryHistoryStateHolder?.state,
      sortDescFirst,
    ],
  );

  return (
    <ResourceFilterTable
      key={tableKey}
      pluralizedResultName="models"
      data={data}
      columns={columns}
      initialSortColumn={SORTABLE_DATA_FIELDS[sortInfo.field]}
      initialSortDesc={sortInfo.direction === SortDirection.Desc}
      pageInfo={pageInfo}
      isLoading={isInitialLoad}
      isLoadingMore={isLoadingMore}
      isSortable={true}
      pageSize={pageSize}
      fetchMore={fetchMore}
      onChangeSort={onChangeSort}
    />
  );
};

const NewModelFilterTable = ({
  data,
  pageInfo,
  sortInfo,
  isInitialLoad,
  isLoadingMore,
  fetchMore,
  pageSize,
}: ModelFilterTableProps) => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const { environmentId, enableModelQueryHistory } = useDbtCloudProject();
  const featureFlags = useFeatureFlags();
  const projectScope = useUrlProjectParams();
  const [, setSearchParams] = useSearchParams();

  const environmentType = projectScope.environmentType;

  const getDetailsPageLink = useLinkToExplorer('resourceDetails');
  const getLinkMapping = useGetLinkMapping();

  const [queryHistoryStateQuery] = useModelQueryHistoryState({ environmentId });
  const queryHistoryStateHolder =
    queryHistoryStateQuery.data?.environment.modelQueryHistoryState;

  const [tableKey, setTableKey] = useState(0);
  const onChangeSort = useCallback(
    (sortRule: SortingState) => {
      setTableKey((previous) => previous + 1);
      if (!sortRule?.length) {
        return;
      }
      const sortCol: string = sortRule[0].id;
      const sortDir = sortRule[0].desc ? SortDirection.Desc : SortDirection.Asc;

      setSearchParams((p) => {
        p.set('sortField', SORTABLE_COLS[sortCol as SortableColumnName]);
        p.set('sortDir', sortDir);
        return p;
      });
    },
    [setSearchParams],
  );

  // TODO: Remove isDevelopment check since only prod environments can be opted in
  const queryHistoryEnabled =
    enableModelQueryHistory && !isDevelopment(environmentType);

  const queryHistoryAvailableNotEnabled =
    hasAccountFeature('explorerQueryHistory') &&
    !queryHistoryEnabled &&
    modelQueryHistorySupported(
      queryHistoryStateQuery.data?.environment.adapterType || '',
      environmentType,
      featureFlags,
    );

  const sortDescFirst =
    queryHistoryStateHolder?.state === ModelQueryHistoryStatus.Success &&
    !queryHistoryAvailableNotEnabled;

  const columns = useMemo(
    () =>
      [
        {
          header: 'Name',
          accessorKey: 'name',
          id: 'name',
          cell: getNameWithHealthLinkCell(
            getDetailsPageLink,
            projectScope,
            getLinkMapping,
          ),
          enableSorting: true,
        },
        {
          header: 'Modeling layer',
          accessorKey: 'modelingLayer',
          id: 'modelingLayer',
          accessorFn: (data) => toTitleCase(data.modelingLayer ?? ''),
          cell: TruncatedCell,
          size: 30,
          minSize: 120,
          enableSorting: false,
        },
        // TODO: [GBB] gate query history META-4358
        ...(queryHistoryEnabled || queryHistoryAvailableNotEnabled
          ? [
              {
                accessorKey: 'usageQueryCount',
                header: () => {
                  return (
                    <QueryHistoryTableHeader
                      queryHistoryState={queryHistoryStateHolder?.state}
                      error={queryHistoryStateHolder?.error}
                      errorCategory={queryHistoryStateHolder?.errorCategory}
                      queryHistoryAvailableNotEnabled={queryHistoryAvailableNotEnabled}
                    />
                  );
                },
                cell: queryHistoryCellConfig(
                  queryHistoryStateHolder?.state,
                  queryHistoryAvailableNotEnabled,
                ),
                meta: {
                  align: queryHistoryAvailableNotEnabled
                    ? ('left' as const)
                    : ('right' as const),
                },
                size: 80,
                minSize: 120,
                enableSorting: sortDescFirst,
                sortDescFirst: sortDescFirst,
              } as ColumnDef<any, any>,
            ]
          : []),
        {
          header: 'Model access',
          accessorKey: 'access',
          accessorFn: (data) => ({
            access: data.access,
          }),
          size: 30,
          minSize: 120,
          enableSorting: false,
          cell: ({
            row: {
              original: { access },
            },
          }) => {
            const accessLevel = access as AccessLevel;
            return (
              <CustomCell tooltip={accessLevel}>
                <span className="flex items-center gap-2">
                  {access && (
                    <AccessLevelIcon
                      size="sm"
                      access={accessLevel}
                      tooltipConfig={{ placement: 'bottom' }}
                      iconClassName="h-4 w-4 text-fgMain"
                    />
                  )}
                  {toTitleCase(accessLevel)}
                </span>
              </CustomCell>
            );
          },
        },
        {
          header: 'Contract enforced',
          accessorKey: 'contractEnforced',
          size: 30,
          minSize: 120,
          enableSorting: false,
        },
        {
          header: 'Owner',
          accessorKey: 'group',
          cell: TruncatedCell,
          size: 60,
          minSize: 120,
          enableSorting: false,
        },
        {
          header: 'Last executed',
          accessorKey: 'lastExecuted',
          cell: TruncatedCell,
          size: 80,
          minSize: 120,
          enableSorting: true,
          sortDescFirst: true,
        },
        {
          header: () => <OneLineHeader title="Row count" align="right" />,
          accessorKey: 'rowCount',
          cell: RightAlignedTruncatedCell,
          size: 30,
          minSize: 120,
          enableSorting: true,
          sortDescFirst: true,
          meta: {
            align: 'right' as const,
          },
        },
      ] as ColumnDef<any, any>[],
    [
      getDetailsPageLink,
      getLinkMapping,
      projectScope,
      queryHistoryAvailableNotEnabled,
      queryHistoryEnabled,
      queryHistoryStateHolder?.error,
      queryHistoryStateHolder?.errorCategory,
      queryHistoryStateHolder?.state,
      sortDescFirst,
    ],
  );

  return (
    <ResourceFilterTable
      key={tableKey}
      pluralizedResultName="models"
      data={data}
      columns={columns}
      initialSortColumn={SORTABLE_DATA_FIELDS[sortInfo.field]}
      initialSortDesc={sortInfo.direction === SortDirection.Desc}
      pageInfo={pageInfo}
      isLoading={isInitialLoad}
      isLoadingMore={isLoadingMore}
      isSortable={true}
      pageSize={pageSize}
      fetchMore={fetchMore}
      onChangeSort={onChangeSort}
    />
  );
};

ModelFilterView.useHasEnterpriseFeatures = useHasEnterpriseFeatures;
